import { Component, h, createRef } from 'preact';
import cx from 'classnames';
import linkState from 'linkstate';

import { Inquiry } from '../../../../../types/inquiries';
import { ReqState } from '../../../../../types/request-state.model';

import getStore from '../../../store';
import * as InquiriesActions from '../../../reducers/inquiries/inquiries.actions';

import { inquiryService } from '../../../../utils/inquiry.service';
import { route } from 'preact-router';
import { InqStageTransitionContent } from '../documents/inq-stage-transition-content.component';
import { HopplerModalizer } from '../../common/hoppler-modalizer.component';

interface SetFirstCallModalProps {
  inquiry: Inquiry;
  onClose?: () => void;
}

interface SetFirstCallModalState {
  reqState?: ReqState;
  date?: string;
  dateReqMsg?: string;
  errorMessage?: string;
  flowMessage?: string;
  selectedFlow?: number;
}

const store = getStore();

export class SetFirstCallModal extends Component<SetFirstCallModalProps, SetFirstCallModalState> {
  constructor(props) {
    super(props);

    this.state = { 
      date: '',
      selectedFlow: null
    }
  }

  flows = [
    'Informed client that chosen properties are available. Viewing to be scheduled.',
    'Informed client that chosen properties are unavailable. Will offer more listings.',
    'Asked client for more information. Viewing list will be prepared.',
    'No initial response from client.',
    'Other'
  ]

  onTransitionProceedClick() {
    this.props.onClose();
    store.dispatch(InquiriesActions.loadStage('made-1st-call'));
    route('/kumita-brokers/inquiries/made-1st-call?actionFrom=ccp01');
  }

  onTransitionCloseClick() {
    // We cannot put this in immediately after the action returns, as there appears
    // to be some caching in the API that causes Set First Call to take about a
    // second to actually reflect in the returned stage results.
    //
    // So we refresh grid data when a transition button is clicked.
    store.dispatch(InquiriesActions.loadStage('accepted'));
    this.props.onClose();
  }

  handleFlowClicked(value: number) {
    this.setState({
      selectedFlow: value
    })
  }

  handleSubmit = async () => {
    const flowIdx = this.state.selectedFlow;
    const msg = this.state.flowMessage;
    const inquiry = this.props.inquiry;

    const payload = {
      date: this.state.date,
      msg: this.state.selectedFlow <= 3 ? this.flows[flowIdx] : msg,
    }

    try {
      this.setState({ reqState: ReqState.IN_PROGRESS });
      await inquiryService.setFirstCall(inquiry.inquiryCode, payload)

      this.setState({
        reqState: ReqState.SUCCESS,
      })

    } catch (error) { 

      let message;
      if (error.response.data && error.response.data.msg) {
        message = error.response.data.msg;
      }
      
      this.setState({
        errorMessage: message || 'Something went wrong.',
        reqState: ReqState.FAILED,
      })
    }
  }

  getModalBody(props: SetFirstCallModalProps, state: SetFirstCallModalState) {
    if (state.reqState !== ReqState.SUCCESS) {
      return (
        <div className="hi-firstcallmdl__scroll-area">
          <p className="hi-firstcallmdl__note">
            <span className="font-weight-bold mr-2">Note:</span>
            Clicking on Set will record today as your 1st call date. Inquiry card
            can't be moved to the next stage unless you choose a conversation
            flow.
          </p>
  
          <p>Kindly select conversation flow:</p> 
  
          { this.getConversationFlowChoices() }
  
          <textarea 
            className={ cx("form-control hi-firstcallmdl__others-text", {
              'd-block': state.selectedFlow === 4
            }) }
            value={ state.flowMessage }
            onInput={ linkState(this, 'flowMessage') }
            placeholder="Write your message here">  
          </textarea>      
  
          <div className="text-center">
            <button
              disabled={ state.reqState == ReqState.IN_PROGRESS || state.selectedFlow == null }
              onClick={ this.handleSubmit } 
              className="btn btn-secondary px-5">
              <span>
                { state.reqState == ReqState.IN_PROGRESS ? 'Setting...' : 'Set' }
              </span>
            </button>
  
            <div className="mt-2">
              { state.reqState == ReqState.FAILED ?
                <small className="text-danger">{ state.errorMessage }</small>
              : null }
            </div>
          </div>
        </div>
      );
    }
  }

  getConfirmationBody(props: SetFirstCallModalProps, state: SetFirstCallModalState) {
    if (state.reqState === ReqState.SUCCESS) {
      return (
        <div className="d-flex align-items-center justify-content-center flex-fill">
          <InqStageTransitionContent targetName='Made 1st Call'
            onClose={ () => this.onTransitionCloseClick() }
            onProceed={ () => this.onTransitionProceedClick() } />
        </div>
      );
    }
  }

  getConversationFlowChoices() {
    return (
      <div className="d-flex flex-column">
        {
          this.flows.map((flow, index: number) => 
            <label className="hi-firstcallmdl__choice-line">
              <input type="radio"
                name="type" 
                onChange={ e => this.handleFlowClicked(index) }
                checked={ this.state.selectedFlow == index }/>
              <span>{ flow }</span>
            </label>
          )
        }
      </div>
    )
  }

  render(props: SetFirstCallModalProps, state: SetFirstCallModalState) {

    return (
      <HopplerModalizer onClose={() => null}>
        <section className="hi-firstcallmdl">
          <div className="hi-firstcallmdl__title-bar">
            <span className="hi-firstcallmdl__title-label">Set 1st Call Date</span>
            
            {/* Just refresh the grid on title bar close click, just in case */}
            <button className="btn btn-default hi-firstcallmdl__title-close" onClick={ () => this.onTransitionCloseClick() }>
              <i className="fas fa-times mr-2"></i>
              Close
            </button>
          </div>

          <div className="hi-firstcallmdl__body">
            { this.getConfirmationBody(props, state) }
            { this.getModalBody(props, state) }
          </div>
        </section>
      </HopplerModalizer>
    )
  }
}
